import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormError } from '@/Hooks';
import { ErrorTooltip, errorTooltipClass } from '@/Components/Controls/ErrorTooltip/ErrorTooltip';
import { ClassNameProps, DisabledProps, InputRefProps } from '@/Types';
import { v4 as uuidV4 } from 'uuid';
import cn from 'classnames';
import styles from './Input.scss';
import { formUtils } from '@/Utils/FormUtils';

type Props<TFormData extends object> = {
  type?: 'integer' | 'currency' | 'weight' | 'size';
  inputClassName?: string;
  placeholder?: string;
  tabIndex?: number;
  autoFocus?: boolean;
} & ClassNameProps &
  DisabledProps &
  InputRefProps<HTMLInputElement, TFormData, string | number>;

export const NumberFormInput = <TFormData extends object>({
  name,
  register,
  rules,
  setValue,
  type = 'currency',
  inputClassName,
  className,
  disabled,
  readOnly,
  error,
  onBlur,
  placeholder,
  tabIndex,
  autoFocus,
}: Props<TFormData>): React.ReactElement => {
  const inputId = useMemo(() => uuidV4(), []);
  const inputRef = register?.(name, rules);
  const inputNodeRef = useRef<HTMLInputElement | null>(null);
  const errorMessage = useFormError(name, error);

  const formatNumber = useCallback((value: number) => {
    switch (type) {
      case 'integer':
        return value.toIntegerString();

      default:
        return value.toDecimalString(undefined, type);
    } // switch
  }, []); // formatNumber

  useEffect(() => {
    if (autoFocus) {
      inputNodeRef?.current?.focus();
    }
  }, [inputNodeRef]);

  // Default value setting
  useEffect(() => {
    if (!inputNodeRef.current) return;

    const input = document.getElementById(inputId) as HTMLInputElement;

    if (input) {
      const initialValue = input.value;
      inputNodeRef.current.value = initialValue ? formatNumber(Number(initialValue)) : initialValue;
    } // if
  }, [inputRef]);

  return (
    <ErrorTooltip error={errorMessage} className={className}>
      <input
        name={`${name}_formatted`}
        ref={inputNodeRef}
        readOnly={readOnly || disabled}
        disabled={disabled}
        tabIndex={tabIndex}
        placeholder={placeholder}
        className={cn(styles.numberInput, inputClassName, {
          [errorTooltipClass]: errorMessage,
        })}
        onKeyPress={(e) => {
          const key = e.key;
          const value = e.currentTarget.value;
          const specialSymbols = [',', '.', '-'];

          // Disable non-numeric symbols
          if (isNaN(Number(key)) && !specialSymbols.includes(key)) {
            e.preventDefault();
            return;
          } // if

          // Switch minus in number
          if (key === '-') {
            e.preventDefault();

            const isNegativeValue = value.includes('-');
            e.currentTarget.value = isNegativeValue ? value.slice(1) : `${key}${value}`;

            return;
          } // if

          // Disable more than one non-integer parts
          if (isNaN(Number(key)) && specialSymbols.includes(key) && (value.includes(',') || value.includes('.'))) {
            e.preventDefault();
            return;
          } // if

          return key;
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            // Switch to next element
            formUtils.switchToNextNode(event, 1);
          }
        }}
        onFocus={(e) => {
          const input = document.getElementById(inputId) as HTMLInputElement;

          if (input) {
            e.target.value = input.value;
          } // if
        }}
        onBlur={(e) => {
          // Checking an empty value
          if (!e.target.value.length) {
            setValue?.(name, '' as any, { shouldValidate: true, shouldDirty: true });
            onBlur?.(e);
            return;
          } // if

          // Delete '.', ',', '-' from the end of the string
          let value: string | number = e.target.value.replace(/[.,-]+$/, '');

          // Сhecking a string for a number
          value = value.replace(',', '.');

          if (value && !isNaN(Number(value))) {
            const normalizedValue = Number(value).toNormalizeNumber(type);

            setValue?.(name, normalizedValue as any, { shouldValidate: true, shouldDirty: true });
            e.target.value = formatNumber(Number(normalizedValue));
            onBlur?.(e);
          } // if
        }}
        title={inputNodeRef?.current?.value}
      />

      <input id={inputId} name={name} {...inputRef} hidden={true} />
    </ErrorTooltip>
  );
};
